import React, { useState, useEffect, useRef } from 'react';
import { analyzeGapWithAI, updateResumeData, submitResumeFeedback } from '../../../../services/resume.service';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CreditBadge from '../common/CreditBadge';

const TargetingGapAnalysisStep = ({ onNavigate, onDataUpdate, uploadedFile, reviewedData, targetRole: propTargetRole, jobDescription: propJobDescription }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    targetRole: propTargetRole || reviewedData?.targetRole || '',
    jobDescription: propJobDescription || reviewedData?.jobDescription || ''
  });
  const [gapAnalysis, setGapAnalysis] = useState(reviewedData?.gap_analysis || null);

  // Feedback State
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackScore, setFeedbackScore] = useState('');
  const [feedbackStars, setFeedbackStars] = useState(0);
  const [feedbackNotes, setFeedbackNotes] = useState('');

  const [hasClickedConstructBefore, setHasClickedConstructBefore] = useState(false);
  const [feedbackBlinking, setFeedbackBlinking] = useState(false);
  const feedbackRef = useRef(null);

  const resumeId = uploadedFile?.id || reviewedData?.id;
  const storageKey = `feedback_FEEDBACK_STEP_3_TARGETING_${resumeId}`;

  useEffect(() => {
    if (resumeId && localStorage.getItem(storageKey)) {
      setFeedbackSubmitted(true);
    }
  }, [resumeId, storageKey]);

  const handleFeedbackSubmit = async () => {
    if (!feedbackScore) {
      toast.error('Please select if the score feels accurate');
      return false;
    }
    if (feedbackStars === 0) {
      toast.error('Please rate if the critical gaps were helpful');
      return false;
    }
    try {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      await submitResumeFeedback({
        user_id: userId,
        category: category,
        resume_id: resumeId || null,
        step: 'FEEDBACK_STEP_3_TARGETING',
        feedback_data: {
          scoreAccurate: feedbackScore,
          stars: feedbackStars,
          notes: feedbackNotes
        }
      });
      setFeedbackSubmitted(true);
      if (resumeId) {
        localStorage.setItem(storageKey, 'true');
      }
      toast.success('Thank you for your feedback!');
      return true;
    } catch (error) {
      toast.error('Failed to submit feedback');
      return false;
    }
  };

  // Sync with reviewedData if it changes (e.g. on mount/refresh)
  useEffect(() => {
    setFormData({
      targetRole: propTargetRole || reviewedData?.targetRole || '',
      jobDescription: propJobDescription || reviewedData?.jobDescription || ''
    });
    if (reviewedData?.gap_analysis) {
      setGapAnalysis(reviewedData.gap_analysis);
    }
  }, [reviewedData, propTargetRole, propJobDescription]);

  // Mutation for AI Gap Analysis
  const analyzeMutation = useMutation({
    mutationFn: async () => {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      const stage = uploadedFile?.stage || 1;
      return await analyzeGapWithAI(reviewedData, formData.targetRole, formData.jobDescription, userId, category, stage);
    },
    onSuccess: (data) => {
      setGapAnalysis(data);
      // Persist to database immediately after analysis success
      saveMutation.mutate(data);
      // Refresh credits UI
      window.dispatchEvent(new CustomEvent('credits-updated'));
    },
    onError: (error) => {
      console.error('Gap analysis failed:', error);
      toast.error('Failed to analyze gap. Please try again.');
    }
  });

  // Mutation for saving to database
  const saveMutation = useMutation({
    mutationFn: async (analysisResult) => {
      if (!reviewedData?.id) return;
      return await updateResumeData(reviewedData.id, {
        gap_analysis: analysisResult,
        step: 'targeting-gap',
        // Also persist the role/JD used for analysis
        targetRole: formData.targetRole,
        jobDescription: formData.jobDescription
      });
    },
    onSuccess: (data, analysisResult) => {
      // Invalidate queries to refresh data if needed
      queryClient.invalidateQueries(['latestResume']);
      toast.success('Analysis result persisted');
      
      // Sync with parent state so it has the latest targetRole/jobDescription
      onDataUpdate({ 
        gapAnalysis: data?.gap_analysis || analysisResult,
        targetRole: formData.targetRole,
        jobDescription: formData.jobDescription
      });
    },
    onError: (error) => {
      console.error('Persistence failed:', error);
    }
  });

  useEffect(() => {
    if (!uploadedFile || !reviewedData) {
      const timer = setTimeout(() => {
        onNavigate('dashboard');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [uploadedFile, reviewedData, onNavigate]);

  if (!uploadedFile || !reviewedData) {
    return (
      <div className="bg-slate-50 flex flex-col items-center justify-center p-8 text-center min-h-screen">
        <div className="bg-white border border-slate-200 rounded-3xl p-12 max-w-md shadow-md">
          <span className="msi text-slate-300 text-[64px] mb-4">description</span>
          <p className="text-slate-600 mb-8 font-inter font-medium">Please complete the review step first.</p>
          <button className="h-[52px] px-8 bg-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[#0055DD] hover:-translate-y-px shadow-lg shadow-blue-500/20 w-full" onClick={() => onNavigate('dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (gapAnalysis) setGapAnalysis(null);
  };

  const handleAnalyzeGap = () => {
    if (!formData.targetRole.trim() && !formData.jobDescription.trim()) {
      toast.error('Please provide either a Target Role OR Job Description.');
      return;
    }
    analyzeMutation.mutate();
  };

  const handleNext = async () => {
    if (!feedbackSubmitted) {
      if (feedbackScore && feedbackStars > 0) {
        const success = await handleFeedbackSubmit();
        if (!success) return;
      } else {
        toast.error('Please complete the feedback section or click "Submit Feedback".');
        if (feedbackRef.current) {
          feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setFeedbackBlinking(true);
          setTimeout(() => setFeedbackBlinking(false), 2000);
        }
        return;
      }
    }

    const nextStep = 'gap-analysis'; // Or whichever the next step is
    onNavigate(nextStep, { 
      uploadedFile, 
      reviewedData: {
        ...reviewedData,
        gap_analysis: gapAnalysis,
        targetRole: formData.targetRole,
        jobDescription: formData.jobDescription
      },
      targetRole: formData.targetRole, 
      jobDescription: formData.jobDescription, 
      gapAnalysis 
    });
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-[10px] cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <button 
              type="button"
              className="flex items-center gap-2 px-4 h-10 bg-white border border-slate-200 rounded-xl text-[14px] font-bold text-slate-600 hover:text-navy hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm whitespace-nowrap"
            >
              <span className="msi text-[18px]">arrow_back</span>
              Back to Dashboard
            </button>
          </div>
          <div className="flex items-center gap-6">
            <CreditBadge />
            <div className="flex items-center gap-3">
              {[
                { n: 1, l: 'Upload', s: 'green' },
                { n: 2, l: 'Review', s: 'green' },
                { n: 3, l: 'Target', s: 'blue' },
                { n: 4, l: 'Finalize', s: 'slate' }
              ].map((step, i) => (
                <React.Fragment key={step.n}>
                  <div className={`flex items-center gap-2 text-[13px] font-semibold ${step.s === 'green' ? 'text-premium-green' : step.s === 'blue' ? 'text-blue' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full ${step.s === 'green' ? 'bg-premium-green text-white' : step.s === 'blue' ? 'border-2 border-blue bg-blue-light text-blue' : 'border-2 border-slate-200 bg-slate-50 text-slate-400'} flex items-center justify-center text-[11px]`}>{step.n}</div>
                    <span className="hidden sm:inline">{step.l}</span>
                  </div>
                  {i < 3 && <div className="w-6 h-[2px] bg-slate-200" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-8 py-10 w-full flex-1">
        <div className="inline-flex items-center gap-1.5 text-slate-400 text-[13px] font-semibold cursor-pointer transition-all hover:text-blue mb-6 font-inter underline-offset-4 hover:underline"
          onClick={() => onNavigate('review', { 
            uploadedFile, 
            reviewedData: {
              ...reviewedData,
              targetRole: formData.targetRole,
              jobDescription: formData.jobDescription
            } 
          })}>
          <span className="msi">arrow_back</span> Back to Review
        </div>

        <div className="flex items-center justify-between mb-10 animate-fade-up">
          <div>
            <h1 className="text-[32px] font-extrabold text-navy tracking-[-0.8px] mb-2 font-montserrat">Targeting & Gap Analysis</h1>
            <p className="text-[15px] text-slate-500 font-inter max-w-2xl leading-relaxed">
              Compare your current resume against your target role to identify critical skill gaps and optimize your transition strategy.
            </p>
          </div>
          {gapAnalysis && (
            <div className="hidden md:flex items-center gap-2.5 px-4 py-2 bg-blue-light border border-blue-100 rounded-full">
              <span className="msi text-blue text-[18px]">auto_awesome</span>
              <span className="text-[12px] font-bold text-blue tracking-tight uppercase">AI Optimization Active</span>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Panel - Input */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm transition-all duration-300 hover:shadow-md p-7 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                  <span className="msi text-[22px]">target</span>
                </div>
                <h2 className="text-[18px] font-bold text-navy font-montserrat tracking-tight">Role Definition</h2>
              </div>

              <div className="space-y-7">
                <div className="group">
                  <label className="block text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-2.5 font-montserrat transition-colors group-focus-within:text-blue">Target Job Title</label>
                  <input
                    type="text"
                    value={formData.targetRole}
                    onChange={(e) => handleInputChange('targetRole', e.target.value)}
                    placeholder="e.g., Senior Product Designer"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm transition-all duration-200 focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light"
                    disabled={analyzeMutation.isPending}
                  />
                </div>

                <div className="group">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[11px] font-extrabold text-slate-400 uppercase tracking-widest font-montserrat transition-colors group-focus-within:text-blue">Job Description</label>
                    {formData.jobDescription && (
                      <button
                        className="text-[11px] text-slate-400 hover:text-red-500 font-bold uppercase tracking-tight flex items-center gap-1 transition-all"
                        onClick={() => handleInputChange('jobDescription', '')}
                      >
                        <span className="msi text-[14px]">delete_sweep</span> Clear
                      </button>
                    )}
                  </div>
                  <textarea
                    value={formData.jobDescription}
                    onChange={(e) => handleInputChange('jobDescription', e.target.value)}
                    placeholder="Paste the full job requirements here for a deeper AI gap analysis..."
                    className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-inter text-sm leading-relaxed transition-all duration-200 focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light resize-none min-h-[220px]"
                    disabled={analyzeMutation.isPending}
                  />
                </div>

                <button
                  onClick={handleAnalyzeGap}
                  disabled={analyzeMutation.isPending || (!formData.targetRole && !formData.jobDescription)}
                  className="h-[52px] px-8 bg-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[#0055DD] hover:-translate-y-px shadow-lg shadow-blue-500/20 disabled:opacity-60 disabled:cursor-wait w-full h-[56px]"
                >
                  {analyzeMutation.isPending ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Simulating ATS Screening...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span className="msi">analytics</span>
                      <span>Begin Gap Analysis</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Results */}
          <div className="lg:col-span-3 space-y-6">
            {!gapAnalysis ? (
              <div className="bg-white border border-slate-200 rounded-3xl shadow-sm transition-all duration-300 hover:shadow-md h-full flex items-center justify-center p-12 text-center animate-fade-up border-dashed" style={{ animationDelay: '0.2s' }}>
                <div className="max-w-sm">
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <span className="msi text-slate-300 text-[32px]">manage_search</span>
                  </div>
                  <h3 className="text-[18px] font-bold text-navy font-montserrat mb-3">AI Engine Ready</h3>
                  <p className="text-[14px] text-slate-500 font-inter leading-relaxed">
                    Provide your target role details on the left, and our AI will perform a real-time comparison to evaluate your ATS compatibility.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6 animate-fade-up">
                <div className="bg-white border border-slate-200 rounded-3xl shadow-sm transition-all duration-300 hover:shadow-md p-7">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-[19px] font-bold text-navy font-montserrat">AI Match Score Card</h2>
                    <div className="text-right">
                      <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest font-montserrat mb-1">Keywords</p>
                      <p className="text-[14px] font-bold text-navy font-inter">{gapAnalysis.skillsMatched} / {gapAnalysis.totalSkillsRequired} Matched</p>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row items-center gap-8 mb-8 p-6 bg-slate-50 border border-slate-200 rounded-3xl">
                    <div className="relative w-32 h-32 shrink-0">
                      <div className="absolute inset-0 flex items-center justify-center flex-col">
                        <span className="text-[28px] font-black text-navy font-montserrat leading-none">{gapAnalysis.atsScore}%</span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-montserrat mt-1">ATS Score</span>
                      </div>
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="64" cy="64" r="58" stroke="white" strokeWidth="8" fill="none" />
                        <circle
                          cx="64" cy="64" r="58"
                          stroke={gapAnalysis.atsScore >= 75 ? '#10B981' : gapAnalysis.atsScore >= 50 ? '#3B82F6' : '#F59E0B'}
                          strokeWidth="8" fill="none" strokeLinecap="round"
                          strokeDasharray={`${gapAnalysis.atsScore * 3.64} 364`}
                        />
                      </svg>
                    </div>

                    <div className="flex-1">
                      <h3 className="text-[18px] font-bold text-navy mb-2 font-montserrat">
                        {gapAnalysis.atsScore >= 80 ? '🎯 Exceptional Match' : gapAnalysis.atsScore >= 60 ? '✅ Promising Match' : '⚠️ Strategic Gap Detected'}
                      </h3>
                      <p className="text-[13.5px] text-slate-600 font-inter leading-relaxed">
                        {gapAnalysis.atsScore >= 80 ? 'Your resume profile shows high precision alignment with this role. Minimal adjustments needed.' :
                          gapAnalysis.atsScore >= 60 ? 'Strong foundation detected. We recommend minor keyword optimization to push your score above 85%.' :
                            'Significant gaps in core terminology found. We will help you reframe your experiences to bridge these gaps.'}
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="p-5 bg-premium-green-light border border-green-100 rounded-2xl">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="msi text-premium-green text-[18px]">verified</span>
                        <h4 className="text-[13px] font-bold text-green-800 uppercase tracking-tight font-montserrat">Matching Strengths</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {gapAnalysis.strengths.slice(0, 5).map((skill, i) => (
                          <span key={i} className="px-3 py-1 bg-white/60 text-[11px] font-bold text-green-700 rounded-lg border border-green-200 capitalize">{skill}</span>
                        ))}
                      </div>
                    </div>

                    <div className="p-5 bg-premium-amber-light border border-amber-100 rounded-2xl">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="msi text-premium-amber text-[18px]">warning</span>
                        <h4 className="text-[13px] font-bold text-amber-800 uppercase tracking-tight font-montserrat">Critical Gaps</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {gapAnalysis.missingSkills.slice(0, 5).map((skill, i) => (
                          <span key={i} className="px-3 py-1 bg-white/60 text-[11px] font-bold text-amber-700 rounded-lg border border-amber-200 capitalize underline underline-offset-2 decoration-amber-300">{skill}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {gapAnalysis.quickWin && (
                  <div className="p-6 bg-gradient-to-r from-blue-500 to-premium-indigo rounded-[24px] text-white shadow-xl shadow-blue-500/20 flex gap-4 animate-fade-up">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
                      <span className="msi text-[24px]">lightbulb</span>
                    </div>
                    <div>
                      <h3 className="text-[16px] font-bold font-montserrat mb-1">AI Quick Win Strategy</h3>
                      <p className="text-[13px] text-blue-50 font-inter leading-relaxed opacity-90">{gapAnalysis.quickWin.suggestion}</p>
                    </div>
                  </div>
                )}

                {!feedbackSubmitted ? (
                  <div 
                    ref={feedbackRef}
                    className={`bg-white border border-slate-200 rounded-3xl p-6 shadow-sm animate-fade-up transition-all duration-300 ${feedbackBlinking ? 'ring-4 ring-blue-500/50 scale-[1.02]' : ''}`}
                  >
                    <h3 className="text-[15px] font-bold text-navy mb-4">Feedback on AI Match Score</h3>
                    <div className="mb-4">
                      <p className="text-[13px] font-semibold text-slate-700 mb-2">Does this ATS score feel accurate to you?</p>
                      <div className="flex flex-wrap gap-2">
                        {['👍 Yes, seems right', '🤔 Not sure', '👎 Doesn\'t reflect my profile'].map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setFeedbackScore(opt)}
                            className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all border ${feedbackScore === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-[13px] font-semibold text-slate-700 mb-2">Were the Critical Gaps helpful?</p>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFeedbackStars(star)}
                            className={`msi text-[28px] transition-colors ${star <= feedbackStars ? 'text-amber-400' : 'text-slate-300 hover:text-amber-200'}`}
                          >
                            {star <= feedbackStars ? 'star' : 'star_border'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="text-[13px] font-semibold text-slate-700 mb-2 block">Anything the AI missed about your profile? (Optional)</label>
                      <input
                        type="text"
                        placeholder="Tell us more..."
                        value={feedbackNotes}
                        onChange={(e) => setFeedbackNotes(e.target.value)}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleFeedbackSubmit}
                      disabled={!feedbackScore || feedbackStars === 0}
                      className="px-5 py-2 bg-blue-500 text-white rounded-xl text-[13px] font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Submit Feedback
                    </button>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                    <span className="msi text-green-600 text-[20px]">check_circle</span>
                    <p className="text-[13.5px] font-semibold text-green-800">Thank you for your feedback! It helps us improve.</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest font-montserrat">Analysis Saved to Session</p>
                  <button onClick={handleNext} className="h-[52px] px-8 bg-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[#0055DD] hover:-translate-y-px shadow-lg shadow-blue-500/20 disabled:opacity-60 disabled:cursor-wait group">
                    <span>Construct Experience</span>
                    <span className="msi group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetingGapAnalysisStep;
